import * as React from 'react';
import { HeartPulse, Power, Trash2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  useAdminDisableApplePayRegistration,
  useAdminDeleteApplePayRegistration,
  useAdminCheckApplePayBtHealth,
  useAdminApplePayAssociationFile,
  useAdminUpdateApplePayAssociationFile,
} from '@/lib/api/apple-pay-registrations';
import { AssociationFileCard } from '@/components/apple-pay/AssociationFileCard';
import { CertificatesSection } from './CertificatesSection';
import { DomainsSection } from './DomainsSection';
import { RegistrationStatusBadge, type ApplePayRegistration } from './utils';

export function RegistrationDetailSheet({
  registration,
  open,
  onOpenChange,
}: {
  registration: ApplePayRegistration | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const disable = useAdminDisableApplePayRegistration();
  const del = useAdminDeleteApplePayRegistration();
  const health = useAdminCheckApplePayBtHealth();
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Association file content is only fetched once the editor is opened.
  const [fileEditorOpened, setFileEditorOpened] = React.useState(false);
  const associationFile = useAdminApplePayAssociationFile(registration?.id ?? '', {
    enabled: fileEditorOpened && !!registration,
  });
  const updateAssociationFile = useAdminUpdateApplePayAssociationFile();

  if (!registration) return null;

  // Only show the health result if it belongs to the registration on screen
  // (the mutation state persists across a different row being opened).
  const healthResult = health.data?.registrationId === registration.id ? health.data : undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center gap-3 pr-8">
            <SheetTitle className="truncate font-mono text-base">
              {registration.merchantIdentifier}
            </SheetTitle>
            <RegistrationStatusBadge status={registration.status} />
          </div>
          <SheetDescription>
            {registration.displayName} ·{' '}
            {registration.isPlatform ? (
              <span className="font-medium">Platform (shared)</span>
            ) : (
              <>
                account <span className="font-mono">{registration.accountId}</span>
              </>
            )}{' '}
            · managed by {registration.managedBy === 'account_manager' ? 'account manager' : 'merchant'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
          <AssociationFileCard
            hasFile={registration.hasAssociationFile}
            domains={registration.domains.map((d) => d.domain)}
            busy={updateAssociationFile.isPending}
            currentContent={associationFile.data?.associationFileContent}
            loadingContent={associationFile.isFetching}
            onOpenEditor={() => setFileEditorOpened(true)}
            onSave={async (content) => {
              await updateAssociationFile.mutateAsync({
                registrationId: registration.id,
                associationFileContent: content,
              });
            }}
          />
          <Separator />
          <CertificatesSection registration={registration} />
          <Separator />
          <DomainsSection registration={registration} />
          <Separator />

          {/* Basis Theory health */}
          <section className="space-y-3">
            <h4 className="text-sm font-semibold">Basis Theory health</h4>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => health.mutate({ registrationId: registration.id })}
                disabled={health.isPending}
              >
                <HeartPulse className="mr-2 h-4 w-4" />
                {health.isPending ? 'Checking…' : 'Check BT registration'}
              </Button>
              {healthResult && (
                <span className="text-sm">
                  {healthResult.existsInBt ? (
                    <span className="text-green-700">
                      Exists in BT
                      {healthResult.merchantIdentifierMatches
                        ? ' · identifier matches'
                        : ' · identifier MISMATCH'}
                    </span>
                  ) : (
                    <span className="text-destructive">Not found in Basis Theory</span>
                  )}
                </span>
              )}
            </div>
          </section>

          <Separator />

          {/* Danger zone */}
          <section className="space-y-3 pb-2">
            <h4 className="text-destructive text-sm font-semibold">Danger zone</h4>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => disable.mutate({ registrationId: registration.id })}
                disabled={disable.isPending || registration.status === 'disabled'}
              >
                <Power className="mr-2 h-4 w-4" />
                {disable.isPending ? 'Disabling…' : 'Disable registration'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                disabled={del.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete registration
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              Disabling deactivates all domains (reversible via re-probe). Deleting removes the registration
              from Basis Theory and our system permanently.
            </p>
          </section>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this Apple Pay registration?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the registration (and its certificates + domains) from Basis Theory
                and our system. Apple Pay via BYOK will stop working for its domains.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(
                    { registrationId: registration.id },
                    {
                      onSuccess: () => {
                        setConfirmDelete(false);
                        onOpenChange(false);
                      },
                    },
                  )
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
